import React, { ReactElement, useCallback, useMemo, useRef, useState } from "react";
import { Modal } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { DateTime } from "luxon";
import {
  ChamberMemberReturn,
  DiscountReturn,
  useDeleteDiscountMutation,
  WhereToRedeemEnum,
} from "../../../graphql/types";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import { useGetOfferedToText } from "../../common/Hooks/useGetOfferedToText";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import "./cmpMemberPages.css";

/**
 * CMP Accelerator design table for "Manage <Company> Perks" (member dashboard, cmpEnabled only).
 * Chamber staff and CPA-only members keep the shared FullPageDiscountTable.
 */
export type CmpMemberPerk = Pick<
  DiscountReturn,
  | "id"
  | "title"
  | "views"
  | "createdAtUtcMilli"
  | "expiryTimeUtcMilli"
  | "redeemSettings"
  | "isPrivate"
  | "memberOnly"
  | "isAvailableToAllMembers"
> & {
  chamberMemberInfo?: Pick<ChamberMemberReturn, "id" | "name" | "logo"> | null;
};

/** Design date format: "Mar 1, 2026". */
export const formatCmpPerkDate = (utcMilli?: number | null): string =>
  typeof utcMilli === "number" && Number.isFinite(utcMilli) && utcMilli > 0
    ? DateTime.fromMillis(utcMilli, { zone: "utc" }).toLocal().toFormat("LLL d, yyyy")
    : "";

export const matchesCmpPerkSearch = (perk: CmpMemberPerk, search: string): boolean => {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return (
    (perk.title || "").toLowerCase().includes(needle) ||
    (perk.chamberMemberInfo?.name || "").toLowerCase().includes(needle)
  );
};

export const CmpPerkSearch = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}): ReactElement => {
  const enumStrings = useEnumStrings();
  const placeholder = enumStrings.memberManagePerks.searchPlaceholder;
  return (
    <div className="cmp-mp-search">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#727272" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
      <input
        type="search"
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
};

const PencilIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth="2" aria-hidden="true">
    <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" />
  </svg>
);
const TrashIcon = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 7h16M9 7V5h6v2M6.5 7l1 13h9l1-13" />
  </svg>
);

export default function CmpMemberPerksTable({
  perks,
  search,
  onDeleteSuccess,
}: {
  perks: CmpMemberPerk[];
  search: string;
  onDeleteSuccess: () => void;
}): ReactElement {
  const history = useHistory();
  const enumStrings = useEnumStrings();
  const getOfferedToText = useGetOfferedToText();
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteDiscount] = useDeleteDiscountMutation();
  const strings = enumStrings.memberManagePerks;

  const methodText = useCallback(
    (whereToRedeem?: WhereToRedeemEnum | null) => {
      switch (whereToRedeem) {
        case WhereToRedeemEnum.InPerson:
          return enumStrings.inPerson;
        case WhereToRedeemEnum.Online:
          return enumStrings.online;
        case WhereToRedeemEnum.ContactCompany:
          return enumStrings.contactCompany;
        default:
          return "-";
      }
    },
    [enumStrings]
  );

  // Same rule as the shared table: sandbox chambers show demo view counts (50-199), kept stable per perk.
  const isSandbox = !!chamberInfo?.isSandbox;
  const sandboxViews = useRef(new Map<string, number>());
  const demoViews = (id: string) => {
    if (!sandboxViews.current.has(id)) sandboxViews.current.set(id, Math.floor(Math.random() * 150) + 50);
    return sandboxViews.current.get(id) as number;
  };
  const rows = useMemo(
    () =>
      perks.map((perk) => {
        const end = perk.expiryTimeUtcMilli || 0;
        return {
          perk,
          expired: end ? end < Date.now() : false,
          redeemableBy: getOfferedToText(perk),
          method: methodText(perk.redeemSettings?.whereToRedeem),
          views: isSandbox ? demoViews(perk.id) : perk.views || 0,
          created: formatCmpPerkDate(perk.createdAtUtcMilli),
          end: formatCmpPerkDate(end),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [perks, getOfferedToText, methodText, isSandbox]
  );
  const visible = rows.filter((row) => matchesCmpPerkSearch(row.perk, search));

  const openUpdate = (perk: CmpMemberPerk) =>
    history.push(RedirectUri.editDiscountChamberMemberAdmin, perk);

  const closeDelete = () => {
    if (deleting) return;
    setDeleteId(null);
    setDeleteError("");
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteDiscount({
        variables: { id: deleteId },
        refetchQueries: ["getAllDiscountsFromChamberMemberId"],
      });
      setDeleteId(null);
      onDeleteSuccess();
    } catch (err) {
      console.error("Error deleting perk:", err);
      setDeleteError("This perk could not be deleted. Please try again. If it keeps failing, contact your chamber.");
    } finally {
      setDeleting(false);
    }
  };

  const headers = [
    strings.perkTitle,
    strings.redeemableBy,
    strings.redemptionMethod,
    strings.views,
    strings.createdDate,
    strings.endDate,
  ];

  return (
    <>
      <div className="cmp-mp-table-scroll">
        <table className="cmp-mp-table" data-testid="cmp-perks-table">
          <thead>
            <tr>
              {headers.map((label) => (
                <th key={label}>{label}</th>
              ))}
              <th className="cmp-mp-sticky">{strings.actions}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.perk.id} onClick={() => openUpdate(row.perk)}>
                <td>
                  <div className="cmp-mp-perk-title">{row.perk.title}</div>
                  <div className={`cmp-mp-perk-status ${row.expired ? "cmp-mp-perk-expired" : "cmp-mp-perk-active"}`}>
                    {row.expired ? "Expired" : "Active"}
                  </div>
                </td>
                <td>{row.redeemableBy}</td>
                <td>{row.method}</td>
                <td>{row.views}</td>
                <td>{row.created}</td>
                <td>{row.end}</td>
                <td className="cmp-mp-sticky" onClick={(e) => e.stopPropagation()}>
                  <div className="cmp-mp-actions">
                    <button type="button" className="cmp-mp-update" onClick={() => openUpdate(row.perk)}>
                      <PencilIcon />
                      {strings.updatePerk}
                    </button>
                    <button type="button" className="cmp-mp-delete" onClick={() => setDeleteId(row.perk.id)}>
                      <TrashIcon />
                      {strings.deletePerk}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td className="cmp-mp-no-match" colSpan={7}>
                  No perks match your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={deleteId !== null}
        onClose={closeDelete}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      >
        <div className="cmp-mp-dialog" role="dialog" aria-modal="true" aria-labelledby="cmp-delete-perk-title">
          <button type="button" className="cmp-mp-dialog-close" aria-label="Close" onClick={closeDelete}>
            ✕
          </button>
          <div className="cmp-mp-dialog-icon">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h16M9 7V5h6v2M6.5 7l1 13h9l1-13M10 11v6M14 11v6" />
            </svg>
          </div>
          <p className="cmp-mp-dialog-title" id="cmp-delete-perk-title">
            {strings.deletePerk}
          </p>
          <p className="cmp-mp-dialog-text">
            {strings.deleteConfirmation}
            <br />
            {strings.cannotBeUndone}
          </p>
          {deleteError && <p className="cmp-mp-dialog-error" role="alert">{deleteError}</p>}
          <div className="cmp-mp-dialog-buttons">
            <button type="button" className="cmp-mp-dialog-delete" onClick={confirmDelete} disabled={deleting}>
              <TrashIcon size={16} />
              {deleting ? "Deleting..." : "Delete"}
            </button>
            <button type="button" className="cmp-mp-dialog-cancel" onClick={closeDelete} disabled={deleting}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
